import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { ArrowLeft, Clock, CheckCircle, XCircle, Ticket } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerReceipts() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [receiptDetails, setReceiptDetails] = useState({});

  useEffect(() => {
    fetchReceipts();
  }, []);

  const fetchReceipts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/receipts/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReceipts(response.data);
      
      // Fetch event and coupon details for each receipt
      for (const receipt of response.data) {
        await fetchReceiptDetails(receipt);
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const fetchReceiptDetails = async (receipt) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch event details
      const eventResponse = await axios.get(`${API}/events/${receipt.event_id}`);
      
      // Fetch coupons for this receipt
      let coupons = [];
      if (receipt.status === 'approved') {
        try {
          const couponsResponse = await axios.get(`${API}/coupons/receipt/${receipt.receipt_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          coupons = couponsResponse.data || [];
        } catch (error) {
          console.log('No coupons found for this receipt');
        }
      }
      
      // Fetch winners if event is completed
      let winners = [];
      if (eventResponse.data.status === 'completed') {
        try {
          const winnersResponse = await axios.get(`${API}/events/${receipt.event_id}/winners`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          winners = winnersResponse.data.winners || [];
        } catch (error) {
          console.log('No winners data available');
        }
      }
      
      setReceiptDetails(prev => ({
        ...prev,
        [receipt.receipt_id]: {
          event: eventResponse.data,
          coupons: coupons,
          winners: winners
        }
      }));
    } catch (error) {
      console.error('Error fetching receipt details:', error);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') return <Badge variant="outline" className="bg-yellow-50"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
  };

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="customer-receipts-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/customer/events')}
          variant="ghost"
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        <h1 className="font-playfair font-bold text-4xl text-primary mb-8">My Receipts</h1>

        <Tabs defaultValue="approved" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="approved">Approved ({receipts.filter(r => r.status === 'approved').length})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({receipts.filter(r => r.status === 'rejected').length})</TabsTrigger>
            <TabsTrigger value="pending">Pending ({receipts.filter(r => r.status === 'pending').length})</TabsTrigger>
          </TabsList>

          {/* Approved Receipts Tab */}
          <TabsContent value="approved">
            {receipts.filter(r => r.status === 'approved').length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No approved receipts yet</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {(receipts || []).filter(r => r.status === 'approved').map((receipt) => (
                  <Card key={receipt.receipt_id} className="p-6" data-testid={`receipt-${receipt.receipt_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-playfair font-bold text-2xl text-primary">${receipt.transaction_amount}</h3>
                          {getStatusBadge(receipt.status)}
                        </div>
                        
                        {receiptDetails[receipt.receipt_id]?.event && (
                          <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex-1">
                                <p className="font-outfit text-sm text-gray-600 mb-1">Event:</p>
                                <p className="font-playfair font-bold text-lg text-primary">
                                  {receiptDetails[receipt.receipt_id].event.event_name}
                                </p>
                                <p className="font-outfit text-sm text-gray-600">
                                  by {receiptDetails[receipt.receipt_id].event.business_name}
                                </p>
                              </div>
                              {receiptDetails[receipt.receipt_id].event.status === 'completed' && (
                                <Badge className="bg-purple-100 text-purple-800 border-purple-300 ml-2">
                                  Draw Complete
                                </Badge>
                              )}
                            </div>
                            
                            {receiptDetails[receipt.receipt_id].event.status === 'completed' && 
                             receiptDetails[receipt.receipt_id].winners && 
                             receiptDetails[receipt.receipt_id].winners.length > 0 && (
                              <div className="mt-3 space-y-3">
                                {/* Check if current user is a winner */}
                                {(() => {
                                  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                                  const userIsWinner = receiptDetails[receipt.receipt_id].winners.find(
                                    w => w.customer_id === currentUser.user_id
                                  );
                                  return userIsWinner && (
                                    <div className="p-3 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-amber-400 rounded-lg">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-2xl">🎉</span>
                                        <p className="font-outfit font-bold text-lg text-amber-800">
                                          Congratulations! You Won!
                                        </p>
                                      </div>
                                      <p className="font-outfit text-sm text-amber-700">
                                        You won <span className="font-bold">{userIsWinner.prize_name}</span> with coupon{' '}
                                        <span className="font-mono font-bold">{userIsWinner.coupon_id}</span>
                                      </p>
                                    </div>
                                  );
                                })()}
                                
                                {/* Winners List */}
                                <div className="p-2 bg-white rounded border border-amber-300">
                                  <p className="font-outfit text-xs font-semibold text-purple-700 mb-2">🏆 Winners Announced:</p>
                                  <div className="space-y-1">
                                    {receiptDetails[receipt.receipt_id].winners.map((winner, idx) => (
                                      <div key={idx} className="flex items-center justify-between text-xs p-2 bg-purple-50 rounded">
                                        <div className="flex-1">
                                          <span className="font-outfit text-gray-700 font-semibold">
                                            {winner.customer_name || 'Anonymous'}
                                          </span>
                                          <span className="text-gray-500 mx-2">•</span>
                                          <span className="font-semibold text-amber-600">
                                            {winner.prize_name}
                                          </span>
                                        </div>
                                        <span className="font-mono text-xs text-purple-600 bg-white px-2 py-1 rounded border border-purple-200">
                                          🎫 {winner.coupon_id}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div className="space-y-1 text-sm font-outfit text-gray-600">
                          <p><strong>Receipt Number:</strong> {receipt.receipt_number || 'N/A'}</p>
                          <p><strong>Business:</strong> {receipt.business_name}</p>
                          <p><strong>Transaction Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</p>
                          <p><strong>Submitted:</strong> {new Date(receipt.submitted_at).toLocaleString()}</p>
                          {receipt.person_helped && (
                            <p><strong>Person Helped:</strong> {receipt.person_helped}</p>
                          )}
                        </div>
                        
                        {receiptDetails[receipt.receipt_id]?.coupons && receiptDetails[receipt.receipt_id].coupons.length > 0 && (
                          <div className="mt-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border-2 border-amber-200">
                            <div className="flex items-center gap-2 mb-3">
                              <Ticket className="w-5 h-5 text-amber-600" />
                              <p className="font-outfit font-semibold text-lg text-gray-800">
                                Your Credits ({receiptDetails[receipt.receipt_id].coupons.length})
                              </p>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {(receiptDetails[receipt.receipt_id]?.coupons || []).map((coupon) => (
                                <div
                                  key={coupon.coupon_id}
                                  className="bg-white border-2 border-dashed border-amber-400 rounded-lg p-3 text-center shadow-sm hover:shadow-md transition-shadow"
                                  data-testid={`coupon-${coupon.coupon_id}`}
                                >
                                  <Ticket className="w-6 h-6 mx-auto mb-2 text-amber-600" />
                                  <p className="font-outfit text-xs text-gray-700 font-mono break-all mb-1">
                                    {coupon.coupon_id}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {coupon.status === 'active' ? '✓ Active' : coupon.status}
                                  </p>
                                  {coupon.status === 'winner' && (
                                    <p className="text-xs text-amber-700 font-bold mt-2 bg-amber-100 rounded px-2 py-1">
                                      🏆 WINNER!
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Rejected Receipts Tab */}
          <TabsContent value="rejected">
            {receipts.filter(r => r.status === 'rejected').length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No rejected receipts</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {(receipts || []).filter(r => r.status === 'rejected').map((receipt) => (
                  <Card key={receipt.receipt_id} className="p-6 border-red-200" data-testid={`receipt-${receipt.receipt_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-playfair font-bold text-2xl text-primary">${receipt.transaction_amount}</h3>
                          {getStatusBadge(receipt.status)}
                        </div>
                        
                        {receiptDetails[receipt.receipt_id]?.event && (
                          <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="font-outfit text-sm text-gray-600 mb-1">Event:</p>
                            <p className="font-playfair font-bold text-lg text-primary">
                              {receiptDetails[receipt.receipt_id].event.event_name}
                            </p>
                            <p className="font-outfit text-sm text-gray-600">
                              by {receiptDetails[receipt.receipt_id].event.business_name}
                            </p>
                          </div>
                        )}
                        
                        <div className="space-y-1 text-sm font-outfit text-gray-600">
                          <p><strong>Receipt Number:</strong> {receipt.receipt_number || 'N/A'}</p>
                          <p><strong>Business:</strong> {receipt.business_name}</p>
                          <p><strong>Transaction Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</p>
                          <p><strong>Submitted:</strong> {new Date(receipt.submitted_at).toLocaleString()}</p>
                          {receipt.person_helped && (
                            <p><strong>Person Helped:</strong> {receipt.person_helped}</p>
                          )}
                        </div>
                        
                        {receipt.rejection_reason && (
                          <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="font-outfit text-sm text-red-600">
                              <strong>Rejection Reason:</strong> {receipt.rejection_reason}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Pending Receipts Tab */}
          <TabsContent value="pending">
            {receipts.filter(r => r.status === 'pending').length === 0 ? (
              <Card className="p-12 text-center">
                <p className="font-outfit text-gray-600">No pending receipts</p>
              </Card>
            ) : (
              <div className="space-y-6">
                {(receipts || []).filter(r => r.status === 'pending').map((receipt) => (
                  <Card key={receipt.receipt_id} className="p-6 border-yellow-200" data-testid={`receipt-${receipt.receipt_id}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-playfair font-bold text-2xl text-primary">${receipt.transaction_amount}</h3>
                          {getStatusBadge(receipt.status)}
                        </div>
                        
                        {receiptDetails[receipt.receipt_id]?.event && (
                          <div className="mb-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="font-outfit text-sm text-gray-600 mb-1">Event:</p>
                            <p className="font-playfair font-bold text-lg text-primary">
                              {receiptDetails[receipt.receipt_id].event.event_name}
                            </p>
                            <p className="font-outfit text-sm text-gray-600">
                              by {receiptDetails[receipt.receipt_id].event.business_name}
                            </p>
                          </div>
                        )}
                        
                        <div className="space-y-1 text-sm font-outfit text-gray-600">
                          <p><strong>Receipt Number:</strong> {receipt.receipt_number || 'N/A'}</p>
                          <p><strong>Business:</strong> {receipt.business_name}</p>
                          <p><strong>Transaction Date:</strong> {new Date(receipt.transaction_date).toLocaleDateString()}</p>
                          <p><strong>Submitted:</strong> {new Date(receipt.submitted_at).toLocaleString()}</p>
                          {receipt.person_helped && (
                            <p><strong>Person Helped:</strong> {receipt.person_helped}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}